interface RequiredEnvVars {
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_ANON_KEY: string;
}

interface OptionalEnvVars {
  VITE_APP_NAME?: string;
  VITE_ENVIRONMENT?: 'development' | 'staging' | 'production';
}

function validateEnvVar(key: string, value: string | undefined): string {
  if (!value || value.trim() === '') {
    throw new Error(
      `Missing required environment variable: ${key}. ` +
      `Please check your .env file and ensure ${key} is properly set.`
    );
  }
  return value.trim();
}

function getRequiredEnvVars(): RequiredEnvVars {
  return {
    VITE_SUPABASE_URL: validateEnvVar('VITE_SUPABASE_URL', import.meta.env.VITE_SUPABASE_URL),
    VITE_SUPABASE_ANON_KEY: validateEnvVar('VITE_SUPABASE_ANON_KEY', import.meta.env.VITE_SUPABASE_ANON_KEY),
  };
}

function getOptionalEnvVars(): OptionalEnvVars {
  return {
    VITE_APP_NAME: import.meta.env.VITE_APP_NAME,
    VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT as 'development' | 'staging' | 'production',
  };
}

// Validate environment variables on module load
export const env = {
  ...getRequiredEnvVars(),
  ...getOptionalEnvVars(),
};

// Helper functions
export const isDevelopment = () => env.VITE_ENVIRONMENT === 'development' || import.meta.env.DEV;
export const isProduction = () => env.VITE_ENVIRONMENT === 'production' || import.meta.env.PROD;

/**
 * True only when connected to the PRODUCTION Supabase project. Mirrors the
 * server-side `assertProductionOdoo()` guard (api/_lib/odoo-sync.ts), which
 * gates Odoo pushes on the same project ref. Use this — not VITE_ENVIRONMENT —
 * to decide whether to show Odoo sync controls, so the UI and server agree by
 * construction and can't drift on an unset env var.
 */
const PRODUCTION_SUPABASE_REF = 'tsmkchtfljmfvwqurbxy';
export const isProductionSupabase = () =>
  env.VITE_SUPABASE_URL.includes(PRODUCTION_SUPABASE_REF);

// Console logging helper that respects environment
export const devLog = (message: string, ...args: unknown[]) => {
  if (isDevelopment()) {
    console.info(`[DEV] ${message}`, ...args);
  }
};

export const devError = (message: string, ...args: unknown[]) => {
  if (isDevelopment()) {
    console.error(`[DEV] ${message}`, ...args);
  }
}; 